/**
 * Margad Stable Collection Card Slider 12/25/2025 (LOCALE-PROOF + SELF-HEALING)
 *
 * Fixes your 3 issues:
 * 1) If theme stops rendering swatches on /tr (or after language switch), this script RE-CREATES swatches
 *    by fetching Shopify product JSON and building material swatches.
 * 2) Vermeil/Silver slide separation uses YOUR metadata FIRST: data-material / data-alt on slide element.
 *    (Only falls back to img alt if needed.)
 * 3) Slider/swipe stays as-is (your working part).
 *
 * Also keeps:
 * - filter.v.option.materyal => auto-select matching swatch
 * - hidden .product-item__selected-variant-id updated
 * - quick view / choose options opens selected variant (data-product-url + product links patched)
 */

(function () {
  'use strict';

  const initializedCards = new WeakSet();
  const originalSlidesCache = new WeakMap(); // card -> pristine clones
  const stateMap = new WeakMap();            // card -> { index, count, keyword }
  let internalDomUpdate = false;

  // cache product JSON per handle to avoid spamming
  const productJsonCache = new Map(); // handle -> Promise<productJson|null>

  // -----------------------
  // Helpers
  // -----------------------
  function getState(card) {
    if (!stateMap.has(card)) stateMap.set(card, { index: 0, count: 0, keyword: '' });
    return stateMap.get(card);
  }
  function qs(card, sel) { return card.querySelector(sel); }
  function qsa(card, sel) { return card.querySelectorAll(sel); }

  function clamp(n, min, max) { return Math.min(Math.max(n, min), max); }
  function isDesktop() { return window.matchMedia('(min-width: 990px)').matches; }

  function normTR(s) {
    return (s || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c');
  }

  function getSlider(card) {
    return qs(card, '[data-product-slider]') || qs(card, '.product-item__image-slider') || null;
  }
  function getTrack(card) {
    const slider = getSlider(card);
    return slider ? (slider.querySelector('.product-item__image-track') || null) : null;
  }
  function sliderWidthPx(card) {
    const slider = getSlider(card);
    return slider ? (slider.getBoundingClientRect().width || 0) : 0;
  }

  // -----------------------
  // URL variant syncing
  // -----------------------
  function withVariantParam(rawUrl, variantId) {
    if (!rawUrl || !variantId) return rawUrl;
    let u;
    try { u = new URL(rawUrl, window.location.origin); } catch (_) { return rawUrl; }
    u.searchParams.set('variant', String(variantId));
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) return u.toString();
    return u.pathname + (u.search ? u.search : '') + (u.hash ? u.hash : '');
  }

  function syncCardVariantLinks(card, variantId) {
    if (!variantId) return;

    card.querySelectorAll('[data-quick-shop-trigger][data-product-url]').forEach((btn) => {
      const current = btn.getAttribute('data-product-url') || '';
      const next = withVariantParam(current, variantId);
      if (next && next !== current) btn.setAttribute('data-product-url', next);
    });

    card.querySelectorAll('a[href*="/products/"]').forEach((a) => {
      const href = a.getAttribute('href') || '';
      if (!href || href.startsWith('#')) return;
      const next = withVariantParam(href, variantId);
      if (next && next !== href) a.setAttribute('href', next);
    });
  }

  // -----------------------
  // Filter parsing -> keyword (locale safe)
  // -----------------------
  function getFilterMaterialTitleFromURL() {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get('filter.v.option.materyal') || '').toString().trim();
    } catch (_) {
      return '';
    }
  }

  function materialKeywordFromText(text) {
    const t = normTR(text);
    if (!t) return '';
    if (t.includes('silver') || t.includes('sterling') || t.includes('gumus') || t.includes('gümüş') || t.includes('925')) return 'silver';
    if (t.includes('rose') || t.includes('pembe')) return 'rose';
    if (t.includes('vermeil') || t.includes('gold') || t.includes('altin') || t.includes('altın') || t.includes('24k') || t.includes('18k')) return 'vermeil';
    return '';
  }

  // -----------------------
  // MATERIAL DETECTION (FIX #2)
  // IMPORTANT: use slide dataset FIRST (your metadata), then fallback to img alt.
  // -----------------------
  function getMaterialFromAlt(slide) {
    const img = slide.querySelector('img');
    const alt = (img?.getAttribute('alt') || '').toLowerCase().trim();
    if (!alt) return '';
    if (alt.includes('silver') || alt.includes('sterling') || alt.includes('925')) return 'silver';
    if (alt.includes('vermeil')) return 'vermeil';
    if (alt.includes('gold') || alt.includes('altin') || alt.includes('altın')) return 'vermeil';
    if (alt.includes('rose')) return 'rose';
    return '';
  }

  function getMaterialForSlide(slide) {
    const ds = (slide?.dataset?.material || slide?.dataset?.alt || '').toLowerCase().trim();
    return ds || getMaterialFromAlt(slide) || '';
  }

  function swatchToKeyword(swatchEl) {
    const handle = (swatchEl?.dataset?.swatch || swatchEl?.dataset?.keyword || '')
      .toLowerCase()
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c');

    if (handle.includes('vermeil') || handle.includes('altin') || handle.includes('altın') || handle.includes('gold')) return 'vermeil';
    if (handle.includes('gumus') || handle.includes('gümüş') || handle.includes('silver') || handle.includes('sterling') || handle.includes('925')) return 'silver';
    if (handle.includes('rose')) return 'rose';
    return handle; // allow direct keyword
  }

  // -----------------------
  // Desktop: primary + hover image swap
  // -----------------------
  function updateDesktopHoverImages(card, keyword) {
    const primaryImg = card.querySelector('.product-item__image--primary img.image__img');
    const secondaryImg = card.querySelector('.product-item__image--secondary img.image__img');
    if (!primaryImg || !secondaryImg) return;

    const originals = originalSlidesCache.get(card) || [];
    const pool = originals.length ? originals : Array.from(card.querySelectorAll('.product-item__image-slide'));

    const matched = pool.filter(sl => getMaterialForSlide(sl) === keyword);
    if (!matched.length) return;

    const first = matched[0]?.querySelector('img');
    const second = (matched[1] || matched[0])?.querySelector('img');

    if (first) {
      primaryImg.src = first.currentSrc || first.src;
      primaryImg.srcset = first.srcset || first.currentSrc || first.src;
    }
    if (second) {
      secondaryImg.src = second.currentSrc || second.src;
      secondaryImg.srcset = second.srcset || second.currentSrc || second.src;
    }
  }

  function bindDesktopHoverFix(card) {
    if (card.dataset.mgDesktopHoverFix === '1') return;
    card.dataset.mgDesktopHoverFix = '1';

    function resync() {
      if (!isDesktop()) return;
      const active = qs(card, '.product-item__swatch--active');
      if (!active) return;
      const keyword = swatchToKeyword(active);
      if (!keyword) return;
      updateDesktopHoverImages(card, keyword);
    }

    card.addEventListener('pointerenter', resync, { passive: true });
    card.addEventListener('mouseenter', resync, { passive: true });
    card.addEventListener('focusin', resync, { passive: true });
  }

  // -----------------------
  // Dots
  // -----------------------
function ensureMgDots(card) {
  const slider = getSlider(card);
  if (!slider) return null;

  // Put dots OUTSIDE the slider (right after it)
  const host = slider.parentElement || card;

  // Look for existing dots as a direct child of host (not inside slider)
  let mg = host.querySelector(':scope > .mg-dots');
  if (mg) return mg;

  mg = document.createElement('div');
  mg.className = 'mg-dots';

  // insert after slider
  slider.insertAdjacentElement('afterend', mg);
  return mg;
}


  function rebuildDots(card, count) {
    const mgDots = ensureMgDots(card);
    if (!mgDots) return;
    mgDots.innerHTML = '';
    if (count <= 1) return;

    for (let i = 0; i < count; i++) {
      const dot = document.createElement('span');
      dot.className = 'mg-dot' + (i === 0 ? ' active' : '');
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        setIndex(card, i, true);
      });
      mgDots.appendChild(dot);
    }
  }

  function setDotsActive(card, index) {
    const mgDots = ensureMgDots(card);
    if (!mgDots) return;
    const dots = mgDots.querySelectorAll('.mg-dot');
    dots.forEach((d, i) => d.classList.toggle('active', i === index));
  }

  // -----------------------
  // Cache slides
  // -----------------------
  function cacheOriginalSlides(card) {
    if (originalSlidesCache.has(card)) return;
    const track = getTrack(card);
    if (!track) return;

    const clones = Array.from(track.querySelectorAll('.product-item__image-slide'))
      .map(node => node.cloneNode(true));

    originalSlidesCache.set(card, clones);
  }

  // -----------------------
  // Rebuild track (max 4)
  // -----------------------
  function rebuildTrack(card, keyword) {
    const track = getTrack(card);
    const originals = originalSlidesCache.get(card) || [];
    if (!track || !originals.length) return;

    const MAX = 4;

    let picked = originals
      .filter(sl => keyword && getMaterialForSlide(sl) === keyword)
      .slice(0, MAX);

    if (!picked.length) picked = originals.slice(0, MAX);

    internalDomUpdate = true;
    try {
      track.innerHTML = '';
      picked.forEach(sl => track.appendChild(sl.cloneNode(true)));
    } finally {
      internalDomUpdate = false;
    }

    const st = getState(card);
    st.keyword = keyword || '';
    st.index = 0;
    st.count = picked.length;

    rebuildDots(card, st.count);
    setIndex(card, 0, false);
  }

  function setIndex(card, index, animate) {
    const track = getTrack(card);
    const st = getState(card);
    if (!track) return;

    const currentSlides = track.querySelectorAll('.product-item__image-slide');
    st.count = currentSlides.length;

    const max = Math.max(0, st.count - 1);
    const safe = clamp(index, 0, max);
    st.index = safe;

    const w = sliderWidthPx(card);
    const x = -safe * w;

    track.style.transition = animate
      ? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      : 'none';

    track.style.transform = `translate3d(${x}px, 0, 0)`;
    setDotsActive(card, safe);

    if (!animate) {
      requestAnimationFrame(() => {
        track.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      });
    }
  }

  // -----------------------
  // Swipe (unchanged)
  // -----------------------
  function bindSwipe(card) {
    const slider = getSlider(card);
    if (!slider) return;

    if (slider.dataset.mgSwipeBound === '1') return;
    slider.dataset.mgSwipeBound = '1';

    const st = getState(card);

    let startX = 0;
    let startY = 0;
    let locked = false;
    let dragging = false;

    slider.addEventListener('touchstart', (e) => {
      if (!e.touches || !e.touches[0]) return;
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      locked = false;
      dragging = true;
    }, { passive: true, capture: true });

    slider.addEventListener('touchmove', (e) => {
      if (!dragging || !e.touches || !e.touches[0]) return;

      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      if (!locked) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (Math.abs(dx) > Math.abs(dy)) locked = true;
        else { dragging = false; return; }
      }

      if (locked) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

        if (Math.abs(dx) > 35) {
          const dir = (dx < 0) ? 1 : -1;
          setIndex(card, st.index + dir, true);
          startX = t.clientX;
          startY = t.clientY;
        }
      }
    }, { passive: false, capture: true });

    slider.addEventListener('touchend', () => {
      dragging = false;
      locked = false;
    }, { passive: true, capture: true });

    window.addEventListener('resize', () => {
      setIndex(card, st.index, false);
    });
  }

  // -----------------------
  // PRODUCT JSON + SWATCH REBUILD (FIX #1)
  // -----------------------
  function extractHandleFromCard(card) {
    // Prefer any product link in the card
    const a = card.querySelector('a[href*="/products/"]');
    if (!a) return '';
    const href = a.getAttribute('href') || '';
    try {
      const u = new URL(href, window.location.origin);
      // /tr/collections/x/products/elysian-eye-kolye or /products/elysian-eye-kolye
      const parts = u.pathname.split('/').filter(Boolean);
      const idx = parts.lastIndexOf('products');
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    } catch (_) {}
    return '';
  }

  function fetchProductJson(handle) {
    if (!handle) return Promise.resolve(null);
    if (productJsonCache.has(handle)) return productJsonCache.get(handle);

    const p = fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
      .then(r => (r && r.ok) ? r.json() : null)
      .catch(() => null);

    productJsonCache.set(handle, p);
    return p;
  }

  function findMaterialOptionIndex(product) {
    // Shopify product JSON has "options": ["Material", "Size"...] (names may vary)
    const opts = product?.options || [];
    for (let i = 0; i < opts.length; i++) {
      const n = normTR(opts[i]);
      if (n.includes('materyal') || n.includes('material') || n.includes('metal')) return i;
    }
    // fallback: try infer by values later
    return -1;
  }

  function valueToKeyword(val) {
    return materialKeywordFromText(val);
  }

  function buildSwatchGroupIfMissing(card, product) {
    // If theme already rendered swatches, do nothing
    if (qs(card, '.product-item__swatch-group')) return;

    if (!product || !Array.isArray(product.variants) || !product.variants.length) return;

    const handle = extractHandleFromCard(card);
    if (!handle) return;

    const optIndex = findMaterialOptionIndex(product);

    // Collect material values -> a representative variant (first available if possible)
    const map = new Map(); // keyword -> { value, variantId }
    for (const v of product.variants) {
      const values = v.options || []; // array in same order as product.options
      let rawVal = '';

      if (optIndex >= 0) rawVal = values[optIndex] || '';
      else {
        // fallback: scan all option values for something that looks like silver/vermeil
        rawVal = values.find(x => valueToKeyword(x)) || '';
      }

      const kw = valueToKeyword(rawVal);
      if (!kw) continue;

      if (!map.has(kw)) map.set(kw, { value: rawVal, variantId: String(v.id) });

      // prefer available variant
      if (v.available && map.get(kw) && !product.variants.find(x => String(x.id) === map.get(kw).variantId && x.available)) {
        map.set(kw, { value: rawVal, variantId: String(v.id) });
      }
    }

    if (!map.size) return;

    // Insert swatch group near title (after h4)
    const title = qs(card, '.product-item__product-title') || qs(card, 'h4');
    const mount = title ? title.parentElement : qs(card, '.product-item__text') || qs(card, '.product-item__meta') || card;
    if (!mount) return;

    const group = document.createElement('div');
    group.className = 'product-item__swatch-group';
    group.setAttribute('data-product-id', String(product.id || card.dataset.productCard || ''));

    // Create buttons in a nice order
    const order = ['vermeil', 'silver', 'rose'];
    const entries = order.filter(k => map.has(k)).map(k => [k, map.get(k)]);
    // add any other keywords not in order
    for (const [k, v] of map.entries()) if (!order.includes(k)) entries.push([k, v]);

    for (const [kw, info] of entries) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'product-item__swatch';
      btn.setAttribute('aria-label', info.value || kw);

      // dataset used by our slider logic
      btn.dataset.keyword = kw;
      btn.dataset.swatch = slugifySwatch(info.value || kw);
      btn.dataset.variantId = info.variantId;
      btn.dataset.variantTitle = info.value || kw;

      const span = document.createElement('span');
      span.className = 'product-item__swatch-display';

      const i = document.createElement('i');
      // your theme likely has these metal swatch classes, we keep the pattern:
      // product-item__metal-swatch--<handle>
      i.className = 'product-item__metal-swatch product-item__metal-swatch--' + slugifySwatch(info.value || kw);

      span.appendChild(i);
      btn.appendChild(span);
      group.appendChild(btn);
    }

    // Insert right after title if possible, otherwise append to mount
    if (title && title.nextSibling) mount.insertBefore(group, title.nextSibling);
    else mount.appendChild(group);

    // Also ensure material label element exists (optional, but helps)
    if (!qs(card, '.product-item__material-name')) {
      const p = document.createElement('p');
      p.className = 'product-item__material-name';
      mount.insertBefore(p, group.nextSibling);
    }

    // Ensure hidden selected variant input exists (optional)
    if (!qs(card, '.product-item__selected-variant-id')) {
      const inp = document.createElement('input');
      inp.type = 'hidden';
      inp.className = 'product-item__selected-variant-id';
      inp.setAttribute('data-product-id', String(product.id || card.dataset.productCard || ''));
      mount.appendChild(inp);
    }
  }

  function slugifySwatch(s) {
    return normTR(s)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  // -----------------------
  // Swatch click binding (works for theme swatches or our rebuilt ones)
  // -----------------------
  function bindSwatches(card) {
    const swatches = qsa(card, '.product-item__swatch');
    if (!swatches.length) return;

    const sig = Array.from(swatches).map(sw => (sw.dataset.variantId || sw.dataset.keyword || '')).join('|');
    if (card.dataset.mgSwatchesSig === sig && card.dataset.mgSwatchesBound === '1') return;
    card.dataset.mgSwatchesSig = sig;
    card.dataset.mgSwatchesBound = '1';

    swatches.forEach(swatch => {
      if (swatch.dataset.mgBound === '1') return;
      swatch.dataset.mgBound = '1';

      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        swatches.forEach(x => x.classList.remove('product-item__swatch--active'));
        swatch.classList.add('product-item__swatch--active');

        const hidden = qs(card, '.product-item__selected-variant-id');
        const vid = swatch.dataset.variantId;
        if (hidden && vid) hidden.value = vid;
        if (vid) syncCardVariantLinks(card, vid);

        const keyword = swatchToKeyword(swatch);

        if (isDesktop()) updateDesktopHoverImages(card, keyword);
        rebuildTrack(card, keyword);
        
        var imgUrl = swatch.dataset.variantImage;
        if (imgUrl) {
          var pImg = card.querySelector('.product-item__image--primary img.image__img');
          if (pImg) { pImg.src = imgUrl; pImg.srcset = imgUrl; }
          var tr = getTrack(card);
          if (tr) {
            var fImg = tr.querySelector('.product-item__image-slide:first-child img');
            if (fImg) { fImg.src = imgUrl; fImg.srcset = imgUrl; }
            // Hover görseli: track'teki 2. slide'dan al
            var allSlideImgs = tr.querySelectorAll('.product-item__image-slide img');
            var sImg = card.querySelector('.product-item__image--secondary img.image__img');
            if (sImg) {
              if (allSlideImgs.length > 1) {
                sImg.src = allSlideImgs[1].currentSrc || allSlideImgs[1].src;
                sImg.srcset = allSlideImgs[1].srcset || allSlideImgs[1].src;
              } else {
                sImg.src = imgUrl;
                sImg.srcset = imgUrl;
              }
            }
          }
        }

        const materialName = swatch.dataset.variantTitle;
        const materialNameEl = qs(card, '.product-item__material-name');
        if (materialNameEl && materialName) materialNameEl.textContent = materialName;
      });
    });
  }

  function pickInitialSwatch(card) {
    const swatches = qsa(card, '.product-item__swatch');
    if (!swatches.length) return null;

    // 1) URL filter
    const filterTitle = getFilterMaterialTitleFromURL();
    const wanted = materialKeywordFromText(filterTitle);
    if (wanted) {
      const hit = Array.from(swatches).find(sw => swatchToKeyword(sw) === wanted);
      if (hit) return hit;
    }

    // 2) keep current active if any
    const active = qs(card, '.product-item__swatch--active');
    if (active) return active;

    // 3) fallback: if slides have silver/vermeil, prefer to match first slide material
    const originals = originalSlidesCache.get(card) || [];
    const firstMat = originals[0] ? getMaterialForSlide(originals[0]) : '';
    if (firstMat) {
      const hit = Array.from(swatches).find(sw => swatchToKeyword(sw) === firstMat);
      if (hit) return hit;
    }

    // 4) default first
    return swatches[0];
  }

  function applySwatchSelection(card, chosen) {
    if (!chosen) return;
    const swatches = qsa(card, '.product-item__swatch');
    swatches.forEach(x => x.classList.remove('product-item__swatch--active'));
    chosen.classList.add('product-item__swatch--active');

    const materialName = chosen.dataset.variantTitle;
    const materialNameEl = qs(card, '.product-item__material-name');
    if (materialNameEl && materialName) materialNameEl.textContent = materialName;

    const hidden = qs(card, '.product-item__selected-variant-id');
    const vid = chosen.dataset.variantId;
    if (hidden && vid) hidden.value = vid;

    if (vid) syncCardVariantLinks(card, vid);

    const kw = swatchToKeyword(chosen);
    if (isDesktop()) updateDesktopHoverImages(card, kw);
    rebuildTrack(card, kw);

    // Variant görseli güncelle
    var imgUrl = chosen.dataset.variantImage;
    var imgUrl = chosen.dataset.variantImage;
    if (imgUrl) {
      var pImg = card.querySelector('.product-item__image--primary img.image__img');
      if (pImg) { pImg.src = imgUrl; pImg.srcset = imgUrl; }
      var tr = getTrack(card);
      if (tr) {
        var fImg = tr.querySelector('.product-item__image-slide:first-child img');
        if (fImg) { fImg.src = imgUrl; fImg.srcset = imgUrl; }
        // Hover görseli: track'teki 2. slide'dan al
        var allSlideImgs = tr.querySelectorAll('.product-item__image-slide img');
        var sImg = card.querySelector('.product-item__image--secondary img.image__img');
        if (sImg) {
          if (allSlideImgs.length > 1) {
            sImg.src = allSlideImgs[1].currentSrc || allSlideImgs[1].src;
            sImg.srcset = allSlideImgs[1].srcset || allSlideImgs[1].src;
          } else {
            sImg.src = imgUrl;
            sImg.srcset = imgUrl;
          }
        }
      }
    }
  }

  // -----------------------
  // Init
  // -----------------------
  async function ensureSwatchesExist(card) {
    // If already exists, nothing to do
    if (qs(card, '.product-item__swatch-group') && qsa(card, '.product-item__swatch').length) return;

    const handle = extractHandleFromCard(card);
    if (!handle) return;

    const product = await fetchProductJson(handle);
    if (!product) return;

    buildSwatchGroupIfMissing(card, product);
  }

  async function initCard(card) {
    // Always ensure slides are cached first (so material split works)
    cacheOriginalSlides(card);

    // Bind swipe once
    bindSwipe(card);
    bindDesktopHoverFix(card);

    // If card is already initialized, we still "self-heal" swatches (language switch rebuild)
    await ensureSwatchesExist(card);

    // Rebind swatches (new DOM = new bindings)
    bindSwatches(card);

    // Choose initial swatch and apply
    const chosen = pickInitialSwatch(card);
    if (chosen) {
      applySwatchSelection(card, chosen);
    } else {
      // No swatches at all: still allow slider split by default material from URL if possible
      const kw = materialKeywordFromText(getFilterMaterialTitleFromURL());
      rebuildTrack(card, kw || '');
    }

    initializedCards.add(card);
  }

  function initAll() {
    if (internalDomUpdate) return;
    document.querySelectorAll('[data-product-card]').forEach((card) => {
      // fire and forget per card (async)
      initCard(card);
    });
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);

  const observer = new MutationObserver((muts) => {
    if (internalDomUpdate) return;
    for (const m of muts) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('[data-product-card]')) initCard(node);
        else node.querySelectorAll?.('[data-product-card]')?.forEach(initCard);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

})();

(function () {
  'use strict';

  function getActiveVariantId(card) {
    if (!card) return null;

    // 1) active swatch (sende bu class var)
    const active = card.querySelector('.product-item__swatch.product-item__swatch--active[data-variant-id]');
    if (active) return active.getAttribute('data-variant-id');

    // 2) fallback: hidden selected variant
    const hidden = card.querySelector('.product-item__selected-variant-id');
    if (hidden && hidden.value) return hidden.value;

    // 3) fallback: first variant input
    const idInput = card.querySelector('input[name="id"]');
    if (idInput && idInput.value) return idInput.value;

    return null;
  }

  function syncQuickAddToVariant(card, variantId) {
    if (!card || !variantId) return;

    // A) Karttaki tüm product forms -> doğru variant id
    card.querySelectorAll('form.product-item__product-form input[name="id"]').forEach((inp) => {
      inp.value = String(variantId);
    });

    // B) Tema quick add -> data-product-id kullanıyor (senin bug burada)
    card.querySelectorAll('button[data-quick-shop-trigger="quick-add"]').forEach((btn) => {
      btn.setAttribute('data-product-id', String(variantId));
      btn.dataset.productId = String(variantId);
    });

    // C) Hidden selected variant sync (zaten var ama garanti)
    const hidden = card.querySelector('.product-item__selected-variant-id');
    if (hidden) hidden.value = String(variantId);
  }

  function syncCard(card) {
    const vid = getActiveVariantId(card);
    if (vid) syncQuickAddToVariant(card, vid);
  }

  function syncAll() {
    document.querySelectorAll('.product-item[data-product-card]').forEach(syncCard);
  }

  // 1) İlk yük + filtre sonrası DOM yenilenmeleri
  document.addEventListener('DOMContentLoaded', syncAll);
  window.addEventListener('pageshow', syncAll);

  // 2) Swatch tıklanınca anında sync
  document.addEventListener('click', function (e) {
    const sw = e.target.closest('.product-item__swatch[data-variant-id]');
    if (!sw) return;

    const card = sw.closest('.product-item[data-product-card]');
    if (!card) return;

    const vid = sw.getAttribute('data-variant-id');
    if (vid) syncQuickAddToVariant(card, vid);
  }, true);

  // 3) Quick add tıklanınca son anda da “zorla” (race condition fix)
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('button[data-quick-shop-trigger="quick-add"]');
    if (!btn) return;

    const card = btn.closest('.product-item[data-product-card]');
    if (!card) return;

    const vid = getActiveVariantId(card);
    if (vid) syncQuickAddToVariant(card, vid);
  }, true);

  // 4) Filter uygulayınca Shopify bazen section’ı rerender ediyor
  const mo = new MutationObserver(() => {
    syncAll();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  (function() {
  function preloadVariantImages() {
    document.querySelectorAll('.product-item__swatch[data-variant-image]').forEach(function(sw) {
      var url = sw.dataset.variantImage;
      if (url) { var img = new Image(); img.src = url; }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', preloadVariantImages);
  } else {
    preloadVariantImages();
  }
})();

})();
